import type { BufferGeometry } from 'three';
import { holdsTracks, TRACKS, trackLifeTicks } from '../../../sim/hazards/tracks';
import type { Biome, World } from '../../../sim/types';
import type { Sky } from '../../../sim/weather';
import type { PixelCamera } from '../../camera/PixelCamera';
import { MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { type Batch, put, yawQuat } from '../effects/batch';

/*
 * The decal layer's footprints (05 §7: "Footprints (arctic/desert), scorch marks, blood, tyre
 * tracks and pod-landing scuffs are stamped into it. Footprints in snow are gameplay-relevant
 * (enemy trackers can follow them)").
 *
 * `ScorchDecals` is the pattern, with one difference that matters: a scorch is stamped once
 * and stays, while a print has to be SEEN to fade, so this batch is rebuilt every frame from
 * `world.hazards.tracks` - the same list the soldiers read. What is drawn is exactly what can
 * be tracked, which is the whole point of putting the trail in the world.
 *
 * There is no alpha anywhere (05 §8.1): a print fades by shrinking in steps, like every other
 * effect here, and vanishes when the sim drops it.
 *
 * ROUND8: the sim lays the trail on EVERY ground now - it is the dog's scent as well as the prints
 * (ROUND8 §1) - but only snow and sand take a print (`holdsTracks`, 03 §4), and only there do
 * soldiers read it. So the prints are drawn only there too: a print on grass would tell the player
 * the garrison can follow it when only the dogs can.
 */

/** How large a print is drawn, as a fraction of full size, over the four quarters of its life. */
const FADE_STEPS = [1, 0.84, 0.66, 0.46] as const;

/** Instances the batch holds: one per sample the sim will ever keep. */
export const MAX_FOOTPRINTS = TRACKS.maxPoints;

/**
 * A pair of boot prints in a unit frame, scaled by the instance as they fade. The darkest step
 * of the ground ramp, which on snow is the blue of a dent and on sand the shadow in a scuff.
 */
export function footprintGeometry(p: Palette): BufferGeometry {
  const c = p.color('ground.0');
  const mb = new MeshBuilder();
  mb.box(0.2, 0.05, 0.38, c, place(-0.14, 0, 0.13), true);
  mb.box(0.2, 0.05, 0.38, c, place(0.14, 0, -0.13), true);
  return mb.build();
}

/**
 * The mission's sky (a storm fades the prints faster, and must fade them on screen too) and its ground. The game
 * hands over the whole `MissionDef`, which has both; without a `biome` nothing is drawn - a print where none can be
 * made is the one thing worse than none.
 */
export type FootprintGround = Sky & { readonly biome?: Biome };

export class FootprintDecals {
  /** Snow or sand: this ground takes a print. */
  private readonly holds: boolean;

  constructor(
    private readonly batch: Batch,
    private readonly groundY: (x: number, z: number) => number,
    private readonly sky: FootprintGround,
  ) {
    this.holds = sky.biome !== undefined && holdsTracks(sky.biome);
  }

  /** Called between `resetBatches` and `flushBatches`, like every other per-frame effect. */
  draw(world: World, camera: PixelCamera): void {
    const trail = world.hazards.tracks;
    if (!this.holds || trail.length === 0) return;
    const life = trackLifeTicks(world.mods, this.sky);
    for (let i = 0; i < trail.length; i++) {
      const p = trail[i];
      if (!p) continue;
      const age = world.tick - p.t;
      if (age < 0 || age >= life) continue;
      const step = FADE_STEPS[Math.min(FADE_STEPS.length - 1, Math.floor((age / life) * FADE_STEPS.length))] ?? 0;
      if (step <= 0) continue;
      // Which way he was walking, from the neighbouring samples: the heading costs nothing to
      // store because the trail itself is the heading.
      const from = trail[i - 1] ?? p;
      const to = trail[i + 1] ?? p;
      const yaw = from === to ? 0 : Math.atan2(to.x - from.x, to.z - from.z);
      put(this.batch, camera, p.x, this.groundY(p.x, p.z) + 0.02, p.z, step, 1, step, yawQuat(yaw));
    }
  }
}
